import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, Search, X, RefreshCw, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Inbound bug-report triage tab. Backed by:
//   GET    /api/admin/bug-reports
//   PATCH  /api/admin/bug-reports/:id   { status, notes? }
//   DELETE /api/admin/bug-reports/:id
const STATUSES = [
  { id: "new",         label: "new",         dot: "bg-[#00E5FF]" },
  { id: "in_progress", label: "in progress", dot: "bg-[#F59E0B]" },
  { id: "resolved",    label: "resolved",    dot: "bg-[#22c55e]" },
  { id: "wontfix",     label: "won't fix",   dot: "bg-zinc-500" },
];

export default function BugsTab() {
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/bug-reports?${params.toString()}`);
      setReports(data.reports || []);
      setCounts(data.counts || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const setStatusFor = async (id, newStatus) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/bug-reports/${id}`, { status: newStatus });
      toast.success(`status → ${newStatus.replace("_", " ")}`);
      setReports((rs) => rs.map((r) => (r.id === id ? { ...r, status: newStatus } : r)));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm("Permanently delete this bug report?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/bug-reports/${id}`);
      toast.success("bug report deleted");
      setReports((rs) => rs.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="bugs-tab">
      {/* Status pills + filter bar */}
      <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
        <StatusPill
          active={status === ""}
          onClick={() => setStatus("")}
          dot="bg-zinc-400"
          label="all"
          count={(counts.new || 0) + (counts.in_progress || 0) + (counts.resolved || 0) + (counts.wontfix || 0)}
        />
        {STATUSES.map((s) => (
          <StatusPill
            key={s.id}
            active={status === s.id}
            onClick={() => setStatus(s.id)}
            dot={s.dot}
            label={s.label}
            count={counts[s.id] || 0}
          />
        ))}
        <div className="relative ml-auto">
          <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search description / email / url"
            className="bg-black border border-zinc-800 pl-7 pr-2 py-1.5 text-zinc-200 focus:border-[#00E5FF] outline-none w-64"
            data-testid="bugs-filter-q"
          />
        </div>
        <button
          onClick={load}
          className="border border-zinc-800 hover:border-[#00E5FF] hover:text-[#00E5FF] text-zinc-400 px-2.5 py-1.5 flex items-center gap-1.5 uppercase tracking-widest"
          data-testid="bugs-refresh"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> refresh
        </button>
      </div>

      {err && (
        <div className="text-xs text-[#EF4444] font-mono border border-[#EF4444]/40 bg-[#EF4444]/10 px-3 py-2" data-testid="bugs-error">
          {err}
        </div>
      )}

      <div className="border border-zinc-900">
        {loading && reports.length === 0 ? (
          <div className="px-3 py-10 text-center text-zinc-500 font-mono text-xs">
            <Loader2 className="inline-block animate-spin mr-2" size={14} /> loading bug reports…
          </div>
        ) : reports.length === 0 ? (
          <div className="px-3 py-12 text-center text-zinc-600 italic font-mono text-xs" data-testid="bugs-empty">
            no reports match the current filters
          </div>
        ) : (
          <ul className="divide-y divide-zinc-900">
            {reports.map((r) => (
              <BugRow
                key={r.id}
                report={r}
                busy={busyId === r.id}
                expanded={openId === r.id}
                onToggle={() => setOpenId(openId === r.id ? null : r.id)}
                onChangeStatus={(s) => setStatusFor(r.id, s)}
                onDelete={() => deleteReport(r.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const StatusPill = ({ active, onClick, dot, label, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-2.5 py-1.5 border uppercase tracking-widest ${
      active
        ? "border-[#00E5FF] text-[#00E5FF]"
        : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
    }`}
    data-testid={`bugs-status-${label.replace(/[^a-z]/g, "")}`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
    {label}
    <span className="text-zinc-500">{count}</span>
  </button>
);

function BugRow({ report, busy, expanded, onToggle, onChangeStatus, onDelete }) {
  const status = report.status || "new";
  const dot = STATUSES.find((s) => s.id === status)?.dot || "bg-zinc-500";
  return (
    <li className="px-3 py-3 hover:bg-zinc-950" data-testid={`bug-row-${report.id}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${dot}`} />
        <button onClick={onToggle} className="flex-1 text-left min-w-0">
          <div className="text-sm text-zinc-200 break-words line-clamp-2 font-mono">
            {report.description}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <span>{fmtTs(report.ts)}</span>
            <span>{report.email || "anon"}</span>
            <span className="text-zinc-600">{status.replace("_", " ")}</span>
          </div>
        </button>
        <div className="flex flex-wrap items-center gap-1.5 shrink-0">
          {STATUSES.filter((s) => s.id !== status).map((s) => (
            <button
              key={s.id}
              onClick={() => onChangeStatus(s.id)}
              disabled={busy}
              className="text-[10px] uppercase tracking-widest font-mono border border-zinc-800 hover:border-[#00E5FF] hover:text-[#00E5FF] text-zinc-400 px-2 py-1 disabled:opacity-40"
              data-testid={`bug-set-${s.id}-${report.id}`}
            >
              → {s.label}
            </button>
          ))}
          <button
            onClick={onDelete}
            disabled={busy}
            className="text-[10px] uppercase tracking-widest font-mono border border-zinc-800 hover:border-[#EF4444] hover:text-[#EF4444] text-zinc-400 px-2 py-1 disabled:opacity-40 flex items-center gap-1"
            data-testid={`bug-delete-${report.id}`}
          >
            <Trash2 size={10} /> del
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 ml-5 space-y-2 text-xs font-mono">
          {report.expected && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">expected</div>
              <div className="text-zinc-300 whitespace-pre-wrap">{report.expected}</div>
            </div>
          )}
          {report.page_url && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">page</div>
              <a href={report.page_url} target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline break-all inline-flex items-center gap-1">
                {report.page_url} <ExternalLink size={10} />
              </a>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
            <KV k="ip" v={report.ip} />
            <KV k="user-agent" v={(report.user_agent || "").slice(0, 80)} />
            <KV k="id" v={report.id} />
            {report.updated_at && <KV k="last updated" v={`${fmtTs(report.updated_at)} by ${report.updated_by || "?"}`} />}
          </div>
        </div>
      )}
    </li>
  );
}

const KV = ({ k, v }) => (
  <div>
    <div className="text-zinc-600">{k}</div>
    <div className="text-zinc-300 normal-case tracking-normal break-all">{v || "—"}</div>
  </div>
);

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}Z`;
  } catch {
    return iso;
  }
}
